import { kineticPerimetryInstruction } from "@constants/instructionConstants";
import {
  AvailablePreTestConfigurationOptions,
  TestType,
  EyeType,
  GoldmannSize,
  TestConfiguration,
  TestCategory,
  ThresholdLevel,
  WaitDuration,
  TestUIConfiguration,
  KineticPerimetryPattern,
} from "types/testTypes";
import { CATEGORY_VERSION_MAP, TEST_TYPE_VERSION_MAP } from "../versionConstants";
import {
  AVAILABLE_EYE_OPTIONS_MONOCULAR,
  CALIBRATION_TOGGLE_OPTION_NEW_APK,
  AVAILABLE_VIRTUAL_ASSISTANT_OPTIONS,
  DUMMY_AVAILABLE_TEST_SPECIFIC_CONFIGURATION_OPTIONS,
} from "../configurationHelperConstants";
import { HeadsetVersion } from "types/productTypes";
import { virtualAssistantKineticConfig } from "@constants/virtualAssistanceConstants";

export const availableKineticPerimetryTestConfigurationOptions: AvailablePreTestConfigurationOptions =
  {
    testTypes: {
      configurationFieldName: "testType",
      choices: [
        {
          value: TestType.AutoKinetic,
          validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.AutoKinetic],
        },
      ],
      isMandatory: true,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.Kinetic],
    },
    eyeConfigurationOptions: {
      ...AVAILABLE_EYE_OPTIONS_MONOCULAR,
      toggleCalibrationOption: { ...CALIBRATION_TOGGLE_OPTION_NEW_APK },
    },
    testSpeedOptions: null,
    languageOptions: null,
    virtualAssistantOptions: { ...AVAILABLE_VIRTUAL_ASSISTANT_OPTIONS },
    testSpecificConfigurationOptions: {
      ...DUMMY_AVAILABLE_TEST_SPECIFIC_CONFIGURATION_OPTIONS,
      kineticDbColors: {
        choices: [
          {
            value: ThresholdLevel.thresholdLevel5,
          },
          {
            value: ThresholdLevel.thresholdLevel10,
          },
          {
            value: ThresholdLevel.thresholdLevel15,
          },
          {
            value: ThresholdLevel.thresholdLevel20,
          },
        ],
        validSinceVersion: null,
        configurationFieldName: "dbColor",
      },
      kineticPatterns: {
        choices: [
          { value: KineticPerimetryPattern.FF_50_12 },
          { value: KineticPerimetryPattern.FF_60_12, validSinceVersion: HeadsetVersion.ver_2_8_1 },
          { value: KineticPerimetryPattern.FF_30_8 },
          { value: KineticPerimetryPattern.FF_CUSTOM, validSinceVersion: HeadsetVersion.ver_2_8_1 },
        ],
        validSinceVersion: HeadsetVersion.ver_2_7_12,
        configurationFieldName: "testPattern",
      },
      stimuliSizeOptions: {
        choices: [
          { value: GoldmannSize.GOLDMANN_III },
          { value: GoldmannSize.GOLDMANN_IV },
          { value: GoldmannSize.GOLDMANN_V },
        ],
        validSinceVersion: null,
        configurationFieldName: "stimuliSize",
      },
      fixationSizeOptions: {
        choices: [
          { value: GoldmannSize.GOLDMANN_III },
          { value: GoldmannSize.GOLDMANN_IV },
          { value: GoldmannSize.GOLDMANN_V },
        ],
        validSinceVersion: null,
        configurationFieldName: "fixationSize",
      },
      testTypeCircleAngleOptions: {
        choices: null,
        selectorType: "numberInput",
        validSinceVersion: HeadsetVersion.ver_2_8_1,
        configurationFieldName: "testTypeCircleAngle",
        isMandatory: false,
      },
      numberOfStimuliStartsOptions: {
        choices: null,
        selectorType: "numberInput",
        validSinceVersion: HeadsetVersion.ver_2_8_1,
        configurationFieldName: "numberOfStimuliStarts",
        isMandatory: false,
      },
    },
  };

export const kineticPerimetryTestConfiguration: TestConfiguration = {
  name: TestCategory.Kinetic,
  testType: TestType.AutoKinetic,
  eyeConfiguration: {
    eyeType: EyeType.RightEye,
    isCalibrationNeeded: true,
    autoPauseForGaze: true,
    isTwoScreenFixationEnabled: true,
    isBackToBackEyeTest: false,
  },
  testSpeed: WaitDuration.Standard,
  testLanguage: null,
  virtualAssistanceConfig: virtualAssistantKineticConfig,
  isPressToStartEnabled: true,
  extraData: {
    testPattern: KineticPerimetryPattern.FF_30_8,
    dbColor: ThresholdLevel.thresholdLevel10,
    stimuliSize: GoldmannSize.GOLDMANN_III,
    fixationSize: GoldmannSize.GOLDMANN_IV,
    speed: 0.507,
    fixationShiftAngle: 0,
    testTypeCircleAngle: 30,
    numberOfStimuliStarts: 8,
  },
};

export const kineticPerimetryUIConfiguration: TestUIConfiguration = {
  showProgressBar: true,
  instructions: kineticPerimetryInstruction,
};
